/**
 * Forms API client — admin (Form Builder) + public (resolved schema for runtime).
 * Mirrors backend Doc 26 §3.4 + apps/auth/api/admin/forms.py.
 */
import { api } from "./client";

// ============================================================================
// Schema types — mirror libs/forms/schema.py
// ============================================================================
export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "datetime"
  | "select"
  | "async_select"
  | "multi_select"
  | "checkbox"
  | "radio"
  | "file"
  | "section"
  | "repeater"
  | "computed";

export interface SelectOption {
  value: string | number | boolean;
  label: string;
}

export interface FieldBinding {
  erp_model: string;
  label_field?: string | null;
  erp_filter?: Record<string, unknown> | null;
  search_fields?: string[] | null;
  order?: string | null;
}

export interface FormFieldSchema {
  name: string;
  type: FieldType;
  label: string;
  label_i18n?: Record<string, string> | null;
  required?: boolean;
  default?: unknown;
  placeholder?: string | null;
  help_text?: string | null;
  min?: number | null;
  max?: number | null;
  min_length?: number | null;
  max_length?: number | null;
  pattern?: string | null;
  options?: SelectOption[] | null;
  binding?: FieldBinding | null;
  min_chars?: number | null;
  show_if?: Record<string, unknown> | null;
  require_if?: Record<string, unknown> | null;
  calc?: Record<string, unknown> | null;
  fields?: FormFieldSchema[] | null;
  min_items?: number | null;
  max_items?: number | null;
  visible_for_roles?: string[] | null;
}

export interface FormSchema {
  code: string;
  title: string;
  description?: string | null;
  fields: FormFieldSchema[];
  submit_label?: string;
  cancel_label?: string;
  locale_default?: "vi" | "en";
}

// ============================================================================
// API DTOs
// ============================================================================
export interface FormVersionMeta {
  id: string;
  version: number;
  status: "draft" | "published" | "archived";
  change_notes: string | null;
  published_at: string | null;
  created_by: string;
  created_at: string;
}

export interface FormVersionDetail extends FormVersionMeta {
  schema: FormSchema;
  logic_rules: Record<string, unknown>;
}

export interface FormDef {
  id: string;
  code: string;
  name: string;
  description: string | null;
  current_version: number;
  published_version: number | null;
  is_archived: boolean;
  versions: FormVersionMeta[];
  updated_at: string;
}

export interface CreateFormBody {
  code: string;
  name: string;
  description?: string | null;
  schema: FormSchema;
  logic_rules?: Record<string, unknown>;
}

export interface SaveDraftBody {
  schema: FormSchema;
  logic_rules?: Record<string, unknown>;
  change_notes?: string | null;
}

export interface PreviewResponse {
  is_valid: boolean;
  errors: Record<string, string>;
  field_states: Record<
    string,
    { visible: boolean; required: boolean; value: unknown }
  >;
  cleaned: Record<string, unknown>;
}

export interface PublishedFormMeta {
  code: string;
  name: string;
  description: string | null;
  version: number;
}

// Resolved schema returned from /api/v1/forms/{code}/resolved
export interface ResolvedFormSchema extends FormSchema {
  version: number;
  // augmented fields có endpoint cho async_select
}

// ============================================================================
// Client
// ============================================================================
export const formsApi = {
  // Admin
  list: (includeArchived = false) =>
    api.get<FormDef[]>("/api/v1/admin/forms", {
      query: { include_archived: includeArchived },
    }),
  create: (body: CreateFormBody) =>
    api.post<FormDef>("/api/v1/admin/forms", body),
  get: (code: string) => api.get<FormDef>(`/api/v1/admin/forms/${code}`),
  getVersion: (code: string, version: number) =>
    api.get<FormVersionDetail>(
      `/api/v1/admin/forms/${code}/versions/${version}`,
    ),
  saveDraft: (code: string, body: SaveDraftBody) =>
    api.post<FormVersionMeta>(`/api/v1/admin/forms/${code}/versions`, body),
  publish: (code: string, version: number) =>
    api.post<FormVersionMeta>(
      `/api/v1/admin/forms/${code}/versions/${version}/publish`,
    ),
  rollback: (code: string, version: number) =>
    api.post<FormVersionMeta>(
      `/api/v1/admin/forms/${code}/rollback/${version}`,
    ),
  archive: (code: string) =>
    api.delete<void>(`/api/v1/admin/forms/${code}`),
  preview: (code: string, payload: Record<string, unknown>, version?: number) =>
    api.post<PreviewResponse>(`/api/v1/admin/forms/${code}/preview`, {
      payload,
      version: version ?? null,
    }),

  // Public
  listPublished: () =>
    api.get<PublishedFormMeta[]>("/api/v1/forms"),
  getResolved: (code: string, version?: number) =>
    api.get<ResolvedFormSchema>(`/api/v1/forms/${code}/resolved`, {
      query: version != null ? { version } : undefined,
    }),
};
